// disOS model: names, option lists (our own wording), program defaults, settings-changed detection, the guarded SB 343
// switch and the engine options (hybrid engine with the published IRS/FTB tables).
import * as engine from '@divorceos/engine';
import {
  TAX_YEARS, defaultCase, defaultSettings,
  type Case, type CaseResult, type CceAllocation, type NetAdjustMethod, type PartyCreditOverride, type PartyIndex, type PartyTaxSettings, type ProgramSettings, type SsMethod, type Tactic, type TacticSub, type TaxYear,
} from '@divorceos/schema';
import enginePkg from '../../../../packages/engine/package.json';
import { isStateAdjusted, stateAdjOf, stateAdjText } from './engineExt';

export const APP = 'disOS';
export const APP_VERSION = 'disOS 2026.1';
export const ENGINE_VERSION = `@divorceos/engine ${enginePkg.version} (published tables)`;

/** Engine options used everywhere in disOS: published IRS and FTB tables, not the Xspouse compatibility tables. */
export const ENGINE_OPTS: engine.EngineOptions = { provider: engine.publishedTablesProvider };

/** Engine options with an explicit `now` (shared with resolveFc4055). */
export const engineOpts = (now: Date): engine.EngineOptions => ({ ...ENGINE_OPTS, now });

export function compute(c: Case): CaseResult {
  return engine.computeCase(c, engineOpts(NOW()));
}

// ------------------------------------------------------------------ FC 4055 version (SB 343), guarded

export type Fc4055Setting = 'AUTO' | 'PRE_SB343' | 'SB343';
export type Fc4055Resolved = engine.Fc4055Resolved;

/** One "now" for the UI and the engine, so the banner and the results agree for 2024 cases under Automatic. */
export const NOW = (): Date => new Date();

/** The version a setting resolves to for this case (the engine's own rule). */
export function resolveFc4055(c: Pick<Case, 'taxYear' | 'settings'>, now: Date = NOW()): Fc4055Resolved {
  return engine.resolveFc4055Version(c, now);
}

/** What the engine applies (same as resolveFc4055 now that the engine carries SB 343). */
export const appliedFc4055 = resolveFc4055;

export const FC4055_OPTIONS: [Fc4055Setting, string][] = [['AUTO', 'Automatic'], ['PRE_SB343', 'Before 9/1/2024'], ['SB343', 'From 9/1/2024']];
export const fcWord = (v: Fc4055Resolved): string => (v === 'SB343' ? 'from' : 'before');

// ------------------------------------------------------------------ option lists

export const SS_OPTIONS: [SsMethod, string][] = [
  ['SANTA_CLARA', 'Santa Clara'], ['ALAMEDA', 'Alameda'], ['MARIN', 'Marin'], ['KINGS', 'Kings'], ['SAN_DIEGO', 'San Diego'],
  ['YOLO', 'Yolo'], ['HUMBOLDT', 'Humboldt'], ['FORMULA', 'User formula'], ['USER', 'User amount'],
];
export const ssName = (m: SsMethod): string => SS_OPTIONS.find((o) => o[0] === m)?.[1] ?? m;

export const CCE_OPTIONS: [CceAllocation, string][] = [
  ['FIFTY_FIFTY', '50/50 between parents'], ['FC4061B', 'FC 4061(b) — by net income (default)'], ['AFTER_SUPPORT', 'By net spendable income'],
  ['CUSTOM', 'Custom percentage'], ['BEFORE_SUPPORT', 'By net income before support'], ['CP_DEDUCTION', 'Deduction for the custodial parent'],
];
/** disOS display label for the engine's add-on-allocation label (keeps the engine's byte-identical result label,
 *  but shows the "net spendable" vocabulary clients expect for the after-support method). */
export const cceDisplayLabel = (engineLabel: string): string => (engineLabel === 'After support' ? 'By net spendable income' : engineLabel);
export const NET_ADJ_OPTIONS: [NetAdjustMethod, string][] = [
  ['ACTUAL_CS', 'Subtract actual child support'], ['PERCENT_ALLOCATED', 'Subtract % of net allocated to child support'],
  ['DOLLAR_ALLOCATED', 'Subtract $ of net allocated to child support'], ['NONE', 'No adjustment'],
];

export const TAX_YEAR_OPTIONS: TaxYear[] = [...TAX_YEARS];

/** Tactics for the Proposed panel, in our words. `A`/`B` are replaced with the party labels. */
export const TACTICS: { id: Tactic; short: string; long: string }[] = [
  { id: '0', short: 'All deductible', long: 'Make the guideline total fully deductible' },
  { id: '1', short: 'Fixed amounts', long: 'Fixed amounts you enter' },
  { id: '2', short: 'NSI target, A', long: 'Lower taxes, then set {A}’s net spendable' },
  { id: '3', short: 'NSI target, B', long: 'Lower taxes, then set {B}’s net spendable' },
  { id: '4', short: '% of combined, A', long: 'Lower taxes, {A} gets a % of combined spendable' },
  { id: '5', short: '% of combined, B', long: 'Lower taxes, {B} gets a % of combined spendable' },
  { id: '6', short: '% of saving, A', long: 'Lower taxes, {A} gets a % of the tax saving' },
  { id: '7', short: '% of saving, B', long: 'Lower taxes, {B} gets a % of the tax saving' },
  { id: '8', short: 'Guideline ratio', long: 'Lower taxes, keep the guideline split of spendable income' },
  { id: '9', short: 'Guideline + releases', long: 'Guideline amounts, best exemption releases' },
];
export const SUB_OPTIONS: [TacticSub, string][] = [
  [0, 'a · Best combined result, one-way payment'], [1, 'b · Best combined result, two-way allowed'], [2, 'c · Cap the deductible part'],
  [3, 'd · Fixed deductible amount'], [4, 'e · Fixed nondeductible amount'], [5, 'f · All-deductible family support'],
];
export const subLetter = (s: TacticSub): string => 'abcdef'[s] ?? '';
export const tacticUsesSub = (t: Tactic): boolean => ['2', '3', '4', '5', '6', '7', '8'].includes(t);
export function tacticLong(t: Tactic, labels: [string, string]): string {
  const d = TACTICS.find((x) => x.id === t)?.long ?? `Tactic ${t}`;
  return d.replace('{A}', labels[0]).replace('{B}', labels[1]);
}
export function tacticShort(t: Tactic, labels: [string, string]): string {
  const d = TACTICS.find((x) => x.id === t)?.short ?? t;
  return d.replace(', A', `, ${labels[0]}`).replace(', B', `, ${labels[1]}`);
}

// ------------------------------------------------------------------ party labels (disOS view preference)

export type LabelPref = 'auto' | 'fm' | 'hw' | 'p12' | 'ab' | 'names';
export type Relation = 'married' | 'rdp' | 'never';
export const LABEL_OPTIONS: [LabelPref, string][] = [
  ['auto', 'Automatic'], ['fm', 'Father / Mother'], ['hw', 'Husband / Wife'], ['p12', 'Partner 1 / Partner 2'], ['ab', 'Party A / Party B'], ['names', 'First names'],
];

export function partyLabels(c: Case, pref: LabelPref): [string, string] {
  let mode: LabelPref = pref;
  if (mode === 'auto') {
    // C25: entered first names win when both are filled, so typing a name just works; otherwise fall back to the
    // relationship-based default (Partner / Party / Father-Mother / Husband-Wife).
    const named = (c.info.partyNames[0] ?? '').trim() && (c.info.partyNames[1] ?? '').trim();
    mode = named ? 'names' : c.settings.domesticPartnership ? 'p12' : c.settings.sameSexMarriage ? 'ab' : c.children.length ? 'fm' : 'hw';
  }
  switch (mode) {
    case 'p12': return ['Partner 1', 'Partner 2'];
    case 'hw': return ['Husband', 'Wife'];
    case 'ab': return ['Party A', 'Party B'];
    case 'names': {
      const first = (s: string, d: string) => (s.trim().split(/\s+/)[0] || d);
      return [first(c.info.partyNames[0] ?? '', 'Party A'), first(c.info.partyNames[1] ?? '', 'Party B')];
    }
    default: return ['Father', 'Mother'];
  }
}

/** The party-label choice saved in case.appData.disOS (read here without the appData module to avoid a cycle). */
function storedLabelPref(c: Case): LabelPref {
  const d = c.appData?.disOS;
  const v = typeof d === 'object' && d !== null ? (d as { labels?: unknown }).labels : undefined;
  return LABEL_OPTIONS.find(([k]) => k === v)?.[0] ?? 'auto';
}

// ------------------------------------------------------------------ program defaults and the settings-changed count

const DEFAULTS_KEY = 'disos.programDefaults';
const SESSION_DEFAULTS_KEY = 'disos.sessionDefaults';

/** disOS shipped defaults: the hybrid defaults plus FC 4055 version Automatic. */
export function shippedSettings(): ProgramSettings {
  // disOS targets current orders, so California conforms to the federal SS rule by default (SB 711, orders on/after 1 Jan 2026).
  return { ...defaultSettings(), fc4055Version: 'AUTO', caConformsAlimony: true };
}

function readJson<T>(store: Storage | undefined, key: string): Partial<T> | null {
  try {
    const raw = store?.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<T>) : null;
  } catch { return null; }
}
const ls = (): Storage | undefined => { try { return globalThis.localStorage; } catch { return undefined; } };
const ss = (): Storage | undefined => { try { return globalThis.sessionStorage; } catch { return undefined; } };

/** The defaults new cases start from and the banner compares against: session > saved > shipped. */
export function programDefaults(): ProgramSettings {
  return { ...shippedSettings(), ...(readJson<ProgramSettings>(ls(), DEFAULTS_KEY) ?? {}), ...(readJson<ProgramSettings>(ss(), SESSION_DEFAULTS_KEY) ?? {}) };
}
export function saveProgramDefaults(s: ProgramSettings | null): void {
  try { if (s) ls()?.setItem(DEFAULTS_KEY, JSON.stringify(s)); else ls()?.removeItem(DEFAULTS_KEY); } catch { /* storage blocked */ }
}
export function saveSessionDefaults(s: ProgramSettings | null): void {
  try { if (s) ss()?.setItem(SESSION_DEFAULTS_KEY, JSON.stringify(s)); else ss()?.removeItem(SESSION_DEFAULTS_KEY); } catch { /* storage blocked */ }
}

export function newDisCase(year: TaxYear): Case {
  const c = defaultCase(year);
  c.settings = programDefaults();
  return c;
}

export interface SettingDef {
  key: string;
  label: string;
  tab: 'cs' | 'ss' | 'tax' | 'tac';
  get: (c: Case) => unknown;
  show: (c: Case) => string;
}

const yn = (b: boolean) => (b ? 'Yes' : 'No');
const S = (c: Case) => c.settings;

/** Every program setting the banner counts, with how to show its value. */
export const SETTING_DEFS: SettingDef[] = [
  { key: 'csMethod', label: 'Child support method', tab: 'cs', get: (c) => S(c).csMethod, show: (c) => (S(c).csMethod === 'USER' ? 'User amount' : 'Guideline') },
  { key: 'fc4055Version', label: 'FC 4055 version', tab: 'cs', get: (c) => S(c).fc4055Version ?? 'PRE_SB343', show: (c) => FC4055_OPTIONS.find((o) => o[0] === (S(c).fc4055Version ?? 'PRE_SB343'))?.[1] ?? '' },
  { key: 'displayLowIncomeRange', label: 'Low-income range display', tab: 'cs', get: (c) => S(c).displayLowIncomeRange, show: (c) => (S(c).displayLowIncomeRange ? 'Shown' : 'Hidden') },
  { key: 'applyCpiToLowIncomeThreshold', label: 'Low-income threshold CPI update', tab: 'cs', get: (c) => S(c).applyCpiToLowIncomeThreshold, show: (c) => yn(S(c).applyCpiToLowIncomeThreshold) },
  { key: 'cceAllocation', label: 'Add-on allocation', tab: 'cs', get: (c) => S(c).cceAllocation, show: (c) => CCE_OPTIONS.find((o) => o[0] === S(c).cceAllocation)?.[1] ?? '' },
  { key: 'csOnAdjustedNets', label: 'Child support on adjusted nets', tab: 'cs', get: (c) => S(c).csOnAdjustedNets, show: (c) => yn(S(c).csOnAdjustedNets) },
  { key: 'csMultiplier', label: 'Child support multiplier', tab: 'cs', get: (c) => S(c).csMultiplier, show: (c) => String(S(c).csMultiplier) },
  { key: 'hardshipLimitBase', label: 'Hardship limit base', tab: 'cs', get: (c) => S(c).hardshipLimitBase, show: (c) => ({ PRESUMED_CS: 'Presumed CS', BASIC_CS: 'Basic CS', CS_ALLOCATION: 'CS allocation' })[S(c).hardshipLimitBase] },
  { key: 'hardshipLimitMultiplier', label: 'Hardship limit %', tab: 'cs', get: (c) => S(c).hardshipLimitMultiplier, show: (c) => S(c).hardshipLimitMultiplier.join('% / ') + '%' },
  { key: 'timeshareMode', label: 'Timeshare worksheet mode', tab: 'cs', get: (c) => S(c).timeshareMode, show: (c) => (S(c).timeshareMode === 'HOURS' ? 'Hours' : 'Santa Clara') },
  { key: 'ssMethod', label: 'Spousal formula', tab: 'ss', get: (c) => S(c).ssMethod, show: (c) => ssName(S(c).ssMethod) },
  { key: 'netAdjustMethod', label: 'Spousal adjustment for CS', tab: 'ss', get: (c) => S(c).netAdjustMethod, show: (c) => NET_ADJ_OPTIONS.find((o) => o[0] === S(c).netAdjustMethod)?.[1] ?? '' },
  { key: 'ssOnAdjustedNets', label: 'Spousal support on adjusted nets', tab: 'ss', get: (c) => S(c).ssOnAdjustedNets, show: (c) => yn(S(c).ssOnAdjustedNets) },
  { key: 'ssMultiplier', label: 'Spousal support multiplier', tab: 'ss', get: (c) => S(c).ssMultiplier, show: (c) => String(S(c).ssMultiplier) },
  { key: 'includeSsAfter2018InFederal', label: 'Spousal deductibility', tab: 'ss', get: (c) => S(c).includeSsAfter2018InFederal, show: (c) => (S(c).includeSsAfter2018InFederal ? 'Deductible (pre-2019 order)' : 'By order date') },
  { key: 'caConformsAlimony', label: 'California spousal support tax (SB 711)', tab: 'ss', get: (c) => S(c).caConformsAlimony, show: (c) => (S(c).caConformsAlimony ? 'Conforms — not deductible/taxable (order 2026+)' : 'Deductible/taxable for CA (pre-2026 order)') },
  { key: 'bumpSs', label: 'Gross-up spousal support for tax', tab: 'ss', get: (c) => S(c).bumpSs, show: (c) => yn(S(c).bumpSs) },
  { key: 'domesticPartnership', label: 'Registered domestic partners', tab: 'tax', get: (c) => S(c).domesticPartnership, show: (c) => yn(S(c).domesticPartnership) },
  { key: 'sameSexMarriage', label: 'Same-sex marriage', tab: 'tax', get: (c) => S(c).sameSexMarriage, show: (c) => yn(S(c).sameSexMarriage) },
  { key: 'releaseExemptions8332', label: 'Exemption releases (Form 8332)', tab: 'tac', get: (c) => S(c).releaseExemptions8332, show: (c) => (S(c).releaseExemptions8332 ? 'Allowed' : 'Not allowed') },
  { key: 'fc4055Checking', label: 'FC 4055 checking', tab: 'cs', get: (c) => S(c).fc4055Checking, show: (c) => yn(S(c).fc4055Checking) },
  { key: 'tactic', label: 'Tactic', tab: 'tac', get: (c) => c.proposed.tactic, show: (c) => `Tactic ${c.proposed.tactic}` },
  { key: 'sub', label: 'Sub-option', tab: 'tac', get: (c) => c.proposed.sub, show: (c) => subLetter(c.proposed.sub) },
  {
    key: 'stateTaxAdjustment', label: 'State tax adjustment', tab: 'tax',
    get: (c) => c.parties.map((p) => { const a = stateAdjOf(p); return isStateAdjusted(a) ? a : null; }),
    show: (c) => { const L = partyLabels(c, storedLabelPref(c)); return c.parties.map((p, i) => `${L[i]} ${stateAdjText(stateAdjOf(p))}`).join(', '); },
  },
  {
    key: 'releaseChildren', label: 'Released children', tab: 'tac', get: (c) => c.proposed.releaseChildren ?? null,
    show: (c) => { const r = c.proposed.releaseChildren ?? []; return r.length ? r.map((i) => c.children[i]?.name || `child ${i + 1}`).join(', ') : 'none'; },
  },
];

export interface ChangedSetting { key: string; label: string; value: string; tab: SettingDef['tab'] }

// Per-party tax toggles and credit overrides (default = on). Used by the banner/report so that
// turning a tax or credit off is disclosed as a changed setting, not shown as "program defaults".
const TAX_SETTING_LABELS: Record<keyof PartyTaxSettings, string> = {
  socialSecurity: 'Social Security', medicare: 'Medicare', selfEmploymentTax: 'Self-employment tax',
  stateIncomeTax: 'State income tax', stateDisabilityInsurance: 'State disability insurance',
};
const CREDIT_OVERRIDE_LABELS: Record<keyof PartyCreditOverride, string> = {
  childCareCredit: 'Child care credit', earnedIncomeCredit: 'Earned income credit', childTaxCredit: 'Child tax credit',
  caExemptionCredit: 'CA exemption credit', caJchhCredit: 'CA joint-custody HoH credit', caRentCredit: 'CA renter credit', caChildCareCredit: 'CA child care credit',
};

/** Settings that differ from the defaults (proposed tactic/sub compare to tactic 9, sub a). */
export function changedSettings(c: Case, defaults: ProgramSettings = programDefaults()): ChangedSetting[] {
  const base = defaultCase(c.taxYear);
  base.settings = { ...defaults };
  const out: ChangedSetting[] = SETTING_DEFS
    .filter((d) => JSON.stringify(d.get(c)) !== JSON.stringify(d.get(base)))
    .map((d) => ({ key: d.key, label: d.label, value: d.show(c), tab: d.tab }));
  // Per-party tax toggles and credit overrides (default is every box on).
  const L = partyLabels(c, storedLabelPref(c));
  c.parties.forEach((p, i) => {
    const taxesOff = (Object.keys(TAX_SETTING_LABELS) as (keyof PartyTaxSettings)[]).filter((k) => p.taxSettings[k] === false).map((k) => TAX_SETTING_LABELS[k]);
    if (taxesOff.length) out.push({ key: `taxSettings:${i}`, label: `Taxes (${L[i]})`, value: `${taxesOff.join(', ')} excluded`, tab: 'tax' });
    const creditsOff = (Object.keys(CREDIT_OVERRIDE_LABELS) as (keyof PartyCreditOverride)[]).filter((k) => p.creditOverride[k] === false).map((k) => CREDIT_OVERRIDE_LABELS[k]);
    if (creditsOff.length) out.push({ key: `creditOverride:${i}`, label: `Credits (${L[i]})`, value: `${creditsOff.join(', ')} off`, tab: 'tax' });
  });
  return out;
}

/** Restore the program settings (and tactic) to the defaults, keeping case facts like domestic partnership. */
export function restoreDefaults(c: Case, defaults: ProgramSettings = programDefaults()): void {
  const keep = { domesticPartnership: c.settings.domesticPartnership, sameSexMarriage: c.settings.sameSexMarriage };
  c.settings = { ...defaults, ...keep };
  c.proposed.tactic = '9';
  c.proposed.sub = 0;
  delete c.proposed.releaseChildren;
  // Reset per-party tax toggles and credit overrides to the shipped defaults.
  const d = defaultCase(c.taxYear);
  c.parties.forEach((p, i) => {
    delete p.stateTaxAdjustment;
    p.taxSettings = { ...d.parties[i].taxSettings };
    p.creditOverride = { ...d.parties[i].creditOverride };
  });
}

// ------------------------------------------------------------------ money helpers

export const factor = (c: Case): number => (c.mode === 'ANNUAL' ? 12 : 1);
export const periodWord = (c: Case): string => (c.mode === 'ANNUAL' ? 'Yearly' : 'Monthly');
/** "1,234" of the absolute rounded value. */
export const nf = (n: number): string => Math.round(Math.abs(n)).toLocaleString('en-US');
/** Amount in the case period, never negative. */
export const amt = (c: Case, monthly: number): string => nf(monthly * factor(c));
export const money = (c: Case, monthly: number): string => `$${amt(c, monthly)}`;
/** Costs in parentheses. */
export const paren = (c: Case, monthly: number): string => { const v = Math.round(monthly * factor(c)); return v < 0 ? `(${nf(v)})` : nf(v); };
export const pct = (n: number, d: number): string => (d ? `${((n / d) * 100).toFixed(1)}%` : '0.0%');
export const other = (p: PartyIndex): PartyIndex => (p === 0 ? 1 : 0);

/** Signed from party 0's point of view: + party 0 pays. */
export const signed = (l: { amount: number; payor: PartyIndex | null }): number => (l.payor === 0 ? l.amount : l.payor === 1 ? -l.amount : 0);
export const payorOfSigned = (v: number): PartyIndex | null => (Math.round(v * 100) === 0 ? null : v > 0 ? 0 : 1);

export function ageAt(dob: string | undefined, year: number): string {
  if (!dob) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob);
  if (!m) return '';
  return String(Math.max(0, year - Number(m[1])));
}
